import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "../i18n";
import { attributionQuery } from "../utils/attribution";
import { HERO_EXPERIMENT } from "../utils/experiment";
import {
  FileText,
  Receipt,
  IdCard,
  Contact,
  Truck,
  FileSignature,
  ShoppingCart,
  UploadCloud,
  Loader2,
  Download,
  Lock,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  UserX,
  FileLock2,
  FileSearch,
  XCircle,
  HelpCircle,
  MinusCircle,
  Landmark,
  Sparkles,
} from "lucide-react";
import { rememberTrial } from "../utils/signup";
import IncomePanel from "./IncomePanel";
import ScanPdfPanel from "./ScanPdfPanel";
import { toSupportedImage, needsConversion } from "../utils/normalizeImage";
import { demoBrowserId } from "../utils/demoBrowserId";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://t4vl5uqv45.execute-api.eu-north-1.amazonaws.com/online";

const appSignupUrl = (lang) => `https://app.whappscan.com/signup?lang=${lang}`;

// Orden pensado para el lector de empresa: primero los documentos de compra y
// venta (factura, albarán, presupuesto, pedido), después ticket, identidad y
// tarjeta. Albarán, presupuesto y pedido se añadieron el 16-9-2026 para las
// páginas por tipo de documento del SEO internacional.
const TEMPLATES = [
  // «Automático» (24-9-2026): detecta el tipo y usa su plantilla, o extrae sin
  // plantilla lo que tenga el documento. Por defecto en la portada y en /free:
  // el visitante no tiene que saber qué pestaña es la suya (un presupuesto
  // subido como factura daba 3 de 6 campos y el visitante se iba).
  { key: "auto", icon: Sparkles },
  { key: "invoice", icon: FileText },
  { key: "deliverynote", icon: Truck },
  { key: "quote", icon: FileSignature },
  { key: "purchaseorder", icon: ShoppingCart },
  // Extractos (24-9-2026): lo que más se repite en Reddit entre asesorías.
  { key: "bankstatement", icon: Landmark },
  { key: "receipt", icon: Receipt },
  { key: "id", icon: IdCard },
  { key: "businesscard", icon: Contact },
];

/**
 * One-click sample documents. Uploading your own invoice — let alone your own
 * ID — to a site you just found in Google is a high-trust action, and the
 * funnel data said almost nobody takes it (1 file selected in 90 days across
 * 174 sessions). A sample lets a sceptic see the real output without exposing
 * anything of their own.
 *
 * These run through the exact same endpoint as a real upload: what you see is
 * genuine model output, not a canned response. The files are text-based PDFs,
 * so `pdf-parse` resolves them without a Textract pass and the result lands in
 * a couple of seconds.
 *
 * No sample for `id` on purpose: fabricating a realistic-looking identity
 * document is not something to ship, and for that template the barrier is
 * privacy anyway, not curiosity — the reassurance copy below the dropzone is
 * what does the work there.
 *
 * Sources for regenerating these live in landing/scripts/samples/.
 */
const SAMPLES = {
  invoice: { path: "/samples/factura-ejemplo.pdf", name: "factura-ejemplo.pdf", type: "application/pdf" },
  receipt: { path: "/samples/ticket-ejemplo.pdf", name: "ticket-ejemplo.pdf", type: "application/pdf" },
  businesscard: { path: "/samples/tarjeta-ejemplo.png", name: "tarjeta-ejemplo.png", type: "image/png" },
  deliverynote: { path: "/samples/delivery-note-sample.pdf", name: "delivery-note-sample.pdf", type: "application/pdf" },
  quote: { path: "/samples/quote-sample.pdf", name: "quote-sample.pdf", type: "application/pdf" },
  purchaseorder: { path: "/samples/purchase-order-sample.pdf", name: "purchase-order-sample.pdf", type: "application/pdf" },
  // «Rechnung prüfen» / VAT invoice checker: a cada una le falta A PROPÓSITO un
  // requisito (Leistungsdatum; dirección del cliente) para que la muestra
  // enseñe un fallo real. Fuentes en landing/scripts/samples/.
  auto: { path: "/samples/factura-ejemplo.pdf", name: "factura-ejemplo.pdf", type: "application/pdf" },
  bankstatement: { path: "/samples/bank-statement-sample.pdf", name: "bank-statement-sample.pdf", type: "application/pdf" },
  invoicecheck_de: { path: "/samples/rechnung-beispiel.pdf", name: "rechnung-beispiel.pdf", type: "application/pdf" },
  invoicecheck_es: { path: "/samples/factura-comprobar-ejemplo.pdf", name: "factura-comprobar-ejemplo.pdf", type: "application/pdf" },
  invoicecheck_uk: { path: "/samples/invoice-sample-uk.pdf", name: "invoice-sample-uk.pdf", type: "application/pdf" },
};

const POLL_TIMEOUT_MS = 150000;

/**
 * Adaptive polling. A typical demo document finishes in ~10 s, so a flat 2.5 s
 * interval added up to 2.5 s of dead time at the end for no reason. Poll fast
 * while the result is likely to land, then back off so a slow document doesn't
 * hammer the endpoint.
 */
function pollDelay(elapsedMs) {
  if (elapsedMs < 8000) return 800;
  if (elapsedMs < 20000) return 1500;
  return 2500;
}

const STATE = {
  IDLE: "idle",
  UPLOADING: "uploading",
  PROCESSING: "processing",
  DONE: "done",
  ERROR: "error",
};

function trackSignup(source) {
  if (window.gtag) window.gtag("event", "signup_click", { method: source });
  if (window.fbq) window.fbq("track", "Lead", { content_name: source });
}

/**
 * Funnel events for the free demo: search -> tool -> see extraction -> signup.
 * Without these the middle of the funnel is invisible in GA4 (we could only see
 * the landing and the signup click, never whether the extraction actually ran).
 */
// Variante del test A/B del hero que se está viendo, o null fuera del hero.
// Módulo, no estado: solo hay un hero por página.
let experimentVariant = null;

function track(event, params) {
  const exp = experimentVariant ? { experiment: HERO_EXPERIMENT, variant: experimentVariant } : {};
  if (window.gtag) window.gtag("event", event, { ...(params || {}), ...exp });
}

/**
 * Self-contained interactive demo widget. Rendered both on the /free page and
 * embedded on the homepage (below the hero). No page chrome / Helmet here.
 */
/**
 * `checkRules` ("de" | "uk") pone el widget en modo «¿cumple mi factura?»: sin
 * selector de plantilla, con la plantilla `invoicecheck_<país>` (escáneres de
 * la demo con `compliance_rules`) y con la lista de requisitos encima de los
 * datos extraídos. Todo lo demás —subida, sondeo, aviso de registro— es igual.
 */
// sampleOverride: muestra propia de una página para su plantilla inicial (la
// página de varias tarjetas enseña una hoja con 8, no la tarjeta suelta).
export default function FreeExtractWidget({ initialTemplate = "auto", variant = null, checkRules = null, sampleOverride = null, incomeCalc = false, onFile = null, resultExtra = null, hideTabs = false, compactTabs = false, resultAfter = null }) {
  const { t, lang } = useTranslation();
  const checkMode = Boolean(checkRules);
  if (checkMode) initialTemplate = `invoicecheck_${checkRules}`;
  // Solo el hero pasa `variant`: en /free y en el blog el visitante no ha visto
  // el mensaje del test, y contarlo mezclaría resultados.
  experimentVariant = variant;
  const [template, setTemplate] = useState(initialTemplate);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [state, setState] = useState(STATE.IDLE);
  const [fields, setFields] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(null);
  // Tipo de documento que ha detectado el backend cuando NO coincide con la
  // pestaña elegida (scanner_processor, `detect_type`). Se calcula en paralelo
  // con la extracción, así que no retrasa el resultado: solo lo acompaña.
  const [suggested, setSuggested] = useState(null);
  // Resultado de la comprobación de requisitos (solo en modo check).
  const [compliance, setCompliance] = useState(null);
  const [notInvoice, setNotInvoice] = useState(false);
  // Comprobación aritmética (scanner_processor, `arithmetic_check`).
  const [arithmetic, setArithmetic] = useState(null);
  // Comprobación de saldo de un extracto (scanner_processor, `balance_check`).
  const [balanceCheck, setBalanceCheck] = useState(null);
  // PDF más largo que la prueba: el backend procesa las primeras páginas y lo dice.
  const [truncated, setTruncated] = useState(null);
  // Qué hizo el modo automático: tipo detectado o extracción sin plantilla.
  const [autoInfo, setAutoInfo] = useState(null);
  // El último fichero enviado, para poder repetir con otra plantilla sin pedirlo
  // de nuevo (las muestras llegan como `overrideFile`, no están en `file`).
  const lastFile = useRef(null);
  // Resultado y plantilla de la última extracción, para llevarlos a la cuenta
  // nueva: el registro los importa y el usuario encuentra ahí su documento.
  const lastRun = useRef({ resultId: null, template: null });
  const inputRef = useRef(null);
  const pollTimer = useRef(null);
  const wallRef = useRef(null);
  const tabsRef = useRef(null);
  const [showTabs, setShowTabs] = useState(false);

  // ¿Llega la gente a ver el muro? Sin esto no se distingue «no lo ven» de
  // «lo ven y no les convence» (28-9-2026).
  useEffect(() => {
    if (state !== STATE.DONE || !wallRef.current || !("IntersectionObserver" in window)) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          track("free_wall_view", { template: lastRun.current.template || template });
          obs.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(wallRef.current);
    return () => obs.disconnect();
  }, [state, template]);

  useEffect(() => () => clearTimeout(pollTimer.current), []);

  // En móvil las pestañas hacen scroll horizontal: en las páginas de DNI la activa («DNI /
  // Pasaporte») quedaba fuera de pantalla y parecía que no había ninguna seleccionada.
  useEffect(() => {
    const box = tabsRef.current;
    const el = box && box.querySelector('[aria-pressed="true"]');
    if (!box || !el) return;
    if (typeof box.scrollTo === "function") box.scrollTo({ left: Math.max(0, el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2) });
  }, [template]);

  const reset = () => {
    setState(STATE.IDLE);
    setFields(null);
    setError(null);
    setPhase(null);
    setSuggested(null);
    setCompliance(null);
    setNotInvoice(false);
    setArithmetic(null);
    setBalanceCheck(null);
    setTruncated(null);
    setAutoInfo(null);
  };

  const pickFile = async (picked) => {
    if (!picked) return;
    let f = picked;
    const ok = f.type === "application/pdf" || f.type.startsWith("image/");
    // WebP, GIF, BMP, AVIF… → JPEG en el navegador (el lector solo admite JPEG, PNG, TIFF y PDF); HEIC fuera de Safari no se puede.
    if (ok && needsConversion(f)) {
      const converted = await toSupportedImage(f);
      if (!converted) {
        track("free_file_rejected", { template, file_type: f.type || "unknown", converted: false });
        setError({ code: "UNSUPPORTED_TYPE" });
        setState(STATE.ERROR);
        return;
      }
      track("free_file_converted", { template, from: f.type });
      f = converted;
    }
    if (!ok) {
      track("free_file_rejected", { template, file_type: f.type || "unknown" });
      setError({ code: "UNSUPPORTED_TYPE" });
      setState(STATE.ERROR);
      return;
    }
    reset();
    track("free_file_selected", { template, file_type: f.type });
    setFile(f);
    if (onFile) onFile(f);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files?.[0]);
  }, []);

  const poll = useCallback((resultId, startedAt, tpl) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/free-extract/result/${resultId}`);
        const data = await res.json();
        // free_result already reports queued/processing; surface it so the
        // button says what is actually happening instead of one frozen label.
        if (data.status === "queued") {
          setPhase("queued");
        } else if (data.status === "processing") {
          setPhase(Date.now() - startedAt < 6000 ? "reading" : "extracting");
        }
        if (data.status === "done") {
          const extracted = data.extracted_json?.fields || [];
          const auto = data.extracted_json?.auto || null;
          track("free_extract_success", {
            template: tpl,
            ...(auto ? { auto_type: auto.routed ? auto.type : "other" } : {}),
            field_count: extracted.length,
            seconds: Math.round((Date.now() - startedAt) / 1000),
          });
          const detected = data.extracted_json?.detected_type;
          const conocido = TEMPLATES.some((x) => x.key === detected);
          if (tpl === "auto") {
            // Ya se ha usado la plantilla adecuada: no hay nada que sugerir.
            setAutoInfo(auto);
          } else if (/^invoicecheck_/.test(tpl)) {
            const c = data.extracted_json?.compliance || { error: true };
            setCompliance(c);
            setArithmetic(data.extracted_json?.arithmetic || null);
            track("free_check_result", {
              template: tpl,
              verdict: c.error ? "error" : c.verdict,
              mode: c.mode || null,
            });
            if (conocido && detected !== "invoice") setNotInvoice(true);
          } else if (conocido && detected !== tpl) {
            setSuggested(detected);
            track("free_type_mismatch", { template: tpl, detected });
          }
          const tr = data.extracted_json?.demo_truncated;
          setTruncated(tr || null);
          if (tr) track("free_demo_truncated", { template: tpl, total: tr.total });
          const bc = data.extracted_json?.balance_check;
          if (bc) {
            setBalanceCheck(bc);
            track("free_balance_check", { template: tpl, ok: bc.ok, rows: bc.rows });
          }
          setFields(extracted);
          setState(STATE.DONE);
          return;
        }
        if (
          data.success === false &&
          data.status !== "queued" &&
          data.status !== "processing"
        ) {
          track("free_extract_error", { template: tpl, code: "PROCESSING_FAILED" });
          setError({ code: "PROCESSING_FAILED", message: data.message });
          setState(STATE.ERROR);
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          track("free_extract_error", { template: tpl, code: "TIMEOUT" });
          setError({ code: "TIMEOUT" });
          setState(STATE.ERROR);
          return;
        }
        poll(resultId, startedAt, tpl);
      } catch (err) {
        track("free_extract_error", { template: tpl, code: "NETWORK" });
        setError({ code: "NETWORK" });
        setState(STATE.ERROR);
      }
    }, pollDelay(Date.now() - startedAt));
  }, []);

  // Takes an explicit file so the sample button can submit the file it just
  // fetched without waiting for a state update to land.
  const submit = async (overrideFile, overrideTemplate) => {
    const target = overrideFile instanceof File ? overrideFile : file;
    if (!target) return;
    const tpl = overrideTemplate || template;
    lastFile.current = target;
    reset();
    track("free_extract_start", {
      template: tpl,
      source: overrideFile instanceof File ? "sample" : "upload",
    });
    setState(STATE.UPLOADING);
    try {
      const form = new FormData();
      form.append("file", target);
      const browserId = demoBrowserId();
      const res = await fetch(
        `${API_URL}/free-extract?template=${encodeURIComponent(tpl)}&lang=${lang}${attributionQuery()}${variant ? `&v=${encodeURIComponent(variant)}` : ""}${browserId ? `&b=${browserId}` : ""}`,
        { method: "POST", body: form }
      );
      const data = await res.json();
      if (res.status === 202 && data.resultId) {
        lastRun.current = { resultId: data.resultId, template: tpl };
        rememberTrial(tpl, data.resultId);
        setState(STATE.PROCESSING);
        setPhase("queued");
        poll(data.resultId, Date.now(), tpl);
        return;
      }
      track("free_extract_error", {
        template: tpl,
        code: data.code || "PROCESSING_FAILED",
      });
      setError({ code: data.code || "PROCESSING_FAILED", message: data.message });
      setState(STATE.ERROR);
    } catch (err) {
      track("free_extract_error", { template, code: "NETWORK" });
      setError({ code: "NETWORK" });
      setState(STATE.ERROR);
    }
  };

  const busy = state === STATE.UPLOADING || state === STATE.PROCESSING;

  const sample = sampleOverride && template === initialTemplate ? sampleOverride : SAMPLES[template];

  const useSample = async () => {
    if (!sample || busy) return;
    track("free_sample_click", { template });
    try {
      const res = await fetch(sample.path);
      if (!res.ok) throw new Error(`sample ${res.status}`);
      const blob = await res.blob();
      const f = new File([blob], sample.name, { type: sample.type });
      setFile(f);
      if (onFile) onFile(f);
      submit(f);
    } catch (err) {
      // Falling back to the normal dropzone is better than a dead button.
      track("free_sample_error", { template });
      setError({ code: "NETWORK" });
      setState(STATE.ERROR);
    }
  };

  /**
   * The API returns field labels from the scanner config in DynamoDB, and those
   * are English-only — so a Spanish visitor saw "Supplier name" at the exact
   * moment the tool is meant to prove its worth. Translate by the stable
   * snake_case field name and fall back to whatever the API sent, so a scanner
   * config change degrades to today's behaviour instead of breaking.
   * `t()` returns the key itself when missing, which is how we detect that.
   */
  const fieldLabel = (f) => {
    const key = `free.fields.${f.name}`;
    const translated = t(key);
    if (translated !== key) return translated;
    if (f.label) return f.label;
    // Columnas del modo automático (sin traducción ni etiqueta): «cas_number»
    // se ve como «Cas number» en vez del nombre técnico.
    const s = String(f.name || "").replace(/_/g, " ").trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  };

  // Los campos de tipo tabla (líneas de un albarán o un pedido) llegan como un
  // array de objetos. Antes se pintaban con JSON.stringify: justo el dato que
  // demuestra que la herramienta lee un albarán salía como código.
  // Valores legibles (1-10-2026): «id_card» y «1964-08-12» son datos, no texto para una persona.
  const fmtScalar = (f, v) => {
    if (f?.name === "document_type") {
      const label = t(`free.docTypes.${v}`, { defaultValue: "" });
      return label || String(v).replace(/_/g, " ");
    }
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      try {
        return new Date(`${v}T12:00:00Z`).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
      } catch (e) {
        return v;
      }
    }
    return String(v);
  };

  const renderValue = (v, f) => {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) {
      if (v.length === 0) return "—";
      // El modelo devuelve las claves de cada fila en orden arbitrario; se
      // ordenan como se lee una línea de albarán o pedido.
      const ORDER = ["full_name", "job_title", "company_name", "phone", "email", "website", "date", "reference", "description", "quantity", "unit", "unit_price", "amount", "balance"];
      const cols = [...new Set(v.flatMap((row) => (row && typeof row === "object" ? Object.keys(row) : [])))].sort(
        (a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99)
      );
      if (cols.length === 0) return v.join(", ");
      return (
        <div className="max-w-full overflow-x-auto">
          <table className="min-w-full text-xs border border-gray-200 rounded-lg">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="px-2 py-1 text-left font-medium whitespace-nowrap">
                    {fieldLabel({ name: c })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {v.map((row, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c} className="px-2 py-1 text-gray-800 align-top">
                      {row?.[c] === null || row?.[c] === undefined || row?.[c] === "" ? "—" : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (typeof v === "object") return JSON.stringify(v);
    return fmtScalar(f, v);
  };

  // Los campos sin dato no ocupan filas con «—»: se resumen debajo (1-10-2026). Una fila vacía
  // por cada campo que el documento no trae (p. ej. «número de soporte» en un DNI alemán)
  // parecía un fallo de la herramienta y alargaba el resultado en el móvil.
  const isEmptyValue = (v) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
  const filledFields = (fields || []).filter((f) => !isEmptyValue(f.value));
  const emptyFields = (fields || []).filter((f) => isEmptyValue(f.value));

  // Registro con este documento ya dentro de la cuenta (signup lo importa).
  // Texto del muro por plantilla. En documentos de identidad, quien está en /it o /es es un
  // alojamiento con un fichero de la policía que generar; el resto (mayoría de visitas de fuera de
  // Europa) no debe leer «Italia» en un muro que no le habla (revisión del 1-10-2026).
  const wallTemplate = lastRun.current.template || template;
  const wallKey = wallTemplate === "id" && (lang === "it" || lang === "es") ? `id_${lang}` : wallTemplate;
  const signupHref = `${appSignupUrl(lang)}${
    lastRun.current.resultId
      ? `&tpl=${encodeURIComponent(lastRun.current.template)}&from=${encodeURIComponent(lastRun.current.resultId)}`
      : ""
  }`;

  return (
    <div className="w-full min-w-0">
      {/* Template picker.

          Móvil (< 640 px): una sola fila de chips con scroll horizontal. En
          rejilla, las 7 plantillas ocupaban 3 filas (~210 px) y empujaban la
          zona de subida fuera de la pantalla en la portada (auditoría visual del
          16-9-2026: en /de/ ni se veían las pestañas completas). El chip
          cortado en el borde indica que hay más.

          Desde 640 px: rejilla de 4 columnas (7 plantillas en 2 filas). */}
      {/* compactTabs (páginas de DNI): la rejilla de 9 plantillas ocupaba tres filas y empujaba la
          zona de subida fuera de la primera pantalla en escritorio. Se pliega tras un enlace. */}
      {compactTabs && !showTabs && !checkMode && !hideTabs && (
        <p className="mb-3 text-center text-sm">
          <button
            type="button"
            onClick={() => setShowTabs(true)}
            disabled={busy}
            className="font-medium text-gray-500 underline decoration-dotted underline-offset-4 hover:text-primary-700"
          >
            {t("free.otherType")}
          </button>
        </p>
      )}
      <div className={checkMode || hideTabs || (compactTabs && !showTabs) ? "hidden" : "-mx-1 mb-4 sm:mx-0 sm:mb-5"}>
        <div ref={tabsRef} className="flex gap-2 overflow-x-auto px-1 pb-1 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TEMPLATES.map(({ key, icon: Icon }) => {
            const active = template === key;
            return (
              <button
                key={key}
                onClick={() => setTemplate(key)}
                disabled={busy}
                aria-pressed={active}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border-2 px-3 py-1.5 transition-all sm:shrink sm:flex-col sm:justify-center sm:rounded-xl sm:px-1 sm:py-2.5 ${
                  active
                    ? "border-primary-500 bg-primary-50 shadow-sm"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <Icon
                  size={17}
                  className={`shrink-0 sm:h-[19px] sm:w-[19px] ${active ? "text-primary-600" : "text-gray-500"}`}
                />
                <span
                  className={`whitespace-nowrap text-xs font-medium leading-tight sm:whitespace-normal sm:text-center sm:text-[11px] ${
                    active ? "text-primary-700" : "text-gray-600"
                  }`}
                >
                  {t(`free.templates.${key}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-5 sm:p-8 text-center transition-all ${
          dragging
            ? "border-primary-500 bg-primary-50"
            : "border-gray-300 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
        <UploadCloud className="mx-auto text-gray-400 mb-2" size={30} />
        {file ? (
          <p className="text-sm text-gray-700 font-medium break-all">
            {file.name}
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-700 font-medium">
              {t("free.drop.title")}
            </p>
            <p className="text-xs text-gray-500 mt-1">{t("free.drop.hint")}</p>
          </>
        )}
      </div>

      {/* Reassurance at the point of decision. It used to live only in the
          disclaimer under the button, i.e. after the moment someone decides
          whether to hand over an invoice or an ID. */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={14} className="text-primary-600" />
          {t("free.trust.noStore")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <UserX size={14} className="text-primary-600" />
          {t("free.trust.noAccount")}
        </span>
        {/* Deliberately a link, not a claim: it gives the sceptic somewhere to
            go. Note we do NOT claim EU-only processing — the free demo routes
            extraction through OpenRouter, so that would not be true. */}
        <a
          href={`/${lang}/gdpr`}
          className="inline-flex items-center gap-1.5 underline decoration-gray-300 hover:text-primary-700"
        >
          <FileLock2 size={14} className="text-primary-600" />
          {t("free.trust.privacy")}
        </a>
      </div>

      {sample && !busy && (
        <button
          onClick={useSample}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-full border-2 border-primary-200 bg-white text-primary-700 py-2.5 text-sm font-semibold hover:bg-primary-50 transition-colors"
        >
          <FileSearch size={16} />
          {checkMode ? t("invoiceCheck.sample") : sample?.labelKey ? t(sample.labelKey) : t(`free.sample.${template}`)}
        </button>
      )}

      <button
        onClick={() => submit()}
        disabled={!file || busy}
        className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-green-500 to-teal-600 text-white py-3 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {busy ? (
          <>
            <Loader2 className="animate-spin" size={18} />
            {state === STATE.UPLOADING
              ? t("free.status.uploading")
              : t(`free.status.${phase || "processing"}`)}
          </>
        ) : checkMode ? (
          t("invoiceCheck.cta")
        ) : (
          t("free.cta.extract")
        )}
      </button>

      <p className="text-center text-xs text-gray-400 mt-2">
        {t("free.disclaimer")}
      </p>

      {/* Error / conversion wall */}
      {state === STATE.ERROR && error && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-sm text-amber-800 font-medium">
                {t(`free.errors.${error.code}`) ||
                  t("free.errors.PROCESSING_FAILED")}
              </p>
              {(error.code === "PAGE_LIMIT" ||
                error.code === "RATE_LIMITED") && (
                <a
                  href={appSignupUrl(lang)}
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackSignup(`free_error_${error.code}`)}
                  className="inline-block mt-3 rounded-full bg-primary-600 text-white text-sm px-4 py-2 hover:bg-primary-700 transition-colors"
                >
                  {t("free.cta.signup")}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {state === STATE.DONE && fields && (
        <div
          className="mt-8"
          // ¿Se llevan el dato copiándolo a mano en vez de registrarse para el Excel? (26-10-2026). Solo la
          // longitud de lo copiado, nunca el texto: es el documento del visitante.
          onCopy={() => {
            const n = String(window.getSelection?.() || "").length;
            if (n) track("free_result_copy", { template: lastRun.current.template || template, chars: n });
          }}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mb-3">
            <CheckCircle2 className="text-primary-600" size={20} />
            <h3 className="text-lg font-semibold text-gray-900">
              {checkMode ? t("invoiceCheck.resultTitle") : t("free.result.title")}
            </h3>
            {/* Lo que la gente quiere al ver el resultado es llevárselo: el botón
                junto al título, no solo en el muro de debajo de una tabla larga
                (27 extracciones → 4 clics de registro en 28 días, 28-9-2026). */}
            {!checkMode && (
              <a
                href={signupHref}
                target="_blank"
                rel="noopener"
                data-testid="download-top"
                onClick={() => trackSignup("free_download_top")}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-green-500 to-teal-600 px-4 py-1.5 text-sm font-semibold text-white shadow hover:shadow-md transition"
              >
                <Download size={15} />
                {t("free.result.downloadTop")}
              </a>
            )}
          </div>

          {filledFields.length === 0 && fields && fields.length > 0 && !truncated && (
            <p data-testid="nothing-read" role="status" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {t("free.result.nothingRead")}
            </p>
          )}

          {checkMode && compliance && (
            <CompliancePanel compliance={compliance} rules={checkRules} notInvoice={notInvoice} t={t} />
          )}
          {checkMode && arithmetic && arithmetic.ok !== null && <ArithmeticPanel ar={arithmetic} t={t} lang={lang} />}
          {checkMode && (
            <h4 className="mt-6 mb-2 text-sm font-semibold text-gray-700">{t("invoiceCheck.extractedTitle")}</h4>
          )}
          {autoInfo && (
            <p data-testid="auto-info" className="mb-4 flex items-start gap-2 rounded-xl border border-primary-100 bg-primary-50/60 p-3 text-sm text-primary-900">
              <Sparkles size={16} className="mt-0.5 shrink-0 text-primary-600" />
              <span>
                {autoInfo.routed
                  ? t("free.auto.detected", { type: t(`free.templates.${autoInfo.type}`) })
                  : t("free.auto.generic", { label: autoInfo.document_label || autoInfo.document_type || "" })}
              </span>
            </p>
          )}
          {truncated && (
            <p data-testid="demo-truncated" className="mb-4 rounded-xl border border-primary-100 bg-primary-50/60 p-3 text-sm text-primary-900">
              {t("free.truncated.text", { processed: truncated.processed, total: truncated.total })}{" "}
              <a href={signupHref} target="_blank" rel="noopener" onClick={() => trackSignup("free_truncated")} className="font-semibold underline">
                {t("free.truncated.cta")}
              </a>
            </p>
          )}
          {/* Con el documento recortado el saldo no puede cuadrar: faltan páginas. */}
          {balanceCheck && !truncated && <BalancePanel bc={balanceCheck} t={t} lang={lang} />}
          {resultExtra && resultExtra(fields)}
          {incomeCalc && (
            <IncomePanel
              t={t}
              truncated={Boolean(truncated)}
              transactions={(fields.find((f) => f.name === "transactions") || {}).value}
            />
          )}

          {/* El backend detecta el tipo mientras extrae. Si no coincide con la
              pestaña, el resultado sale igual (nunca se bloquea) pero se ofrece
              repetir con la plantilla correcta, que es de donde venían los
              resultados medio vacíos. */}
          {suggested && (
            <div data-testid="type-mismatch" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4">
              <p className="text-sm text-amber-900 flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>
                  {t("free.mismatch.text", {
                    detected: t(`free.templates.${suggested}`),
                  })}
                </span>
              </p>
              <button
                type="button"
                onClick={() => {
                  track("free_type_retry", { from: template, to: suggested });
                  setTemplate(suggested);
                  submit(lastFile.current, suggested);
                }}
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 transition"
              >
                {t("free.mismatch.retry", {
                  template: t(`free.templates.${suggested}`),
                })}
              </button>
              <p className="mt-2 text-xs text-amber-800/80">{t("free.mismatch.note")}</p>
            </div>
          )}

          {/* table-fixed: con el diseño automático, la tabla de líneas anidada
              imponía su ancho a toda la tabla de resultados. Fijo, cada celda
              respeta el ancho disponible y las líneas hacen scroll dentro. */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[44%]" />
                <col />
              </colgroup>
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-3 sm:px-4 py-2 font-medium">
                    {t("free.result.field")}
                  </th>
                  <th className="px-3 sm:px-4 py-2 font-medium">
                    {t("free.result.value")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filledFields.map((f, i) =>
                  Array.isArray(f.value) ? (
                    // Las líneas ocupan la fila entera: en móvil, al lado de la
                    // etiqueta no caben 5 columnas. La tabla lleva su propio
                    // scroll horizontal, no el de todo el resultado.
                    <tr key={i}>
                      <td colSpan={2} className="px-3 sm:px-4 py-2">
                        <div className="text-gray-500 mb-1.5">{fieldLabel(f)}</div>
                        {renderValue(f.value)}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-4 py-2 text-gray-500 hyphens-auto">
                        {fieldLabel(f)}
                      </td>
                      <td className="px-3 sm:px-4 py-2 text-gray-900 font-medium break-words">
                        {renderValue(f.value, f)}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {emptyFields.length > 0 && (
            <details className="mt-2 text-xs text-gray-500">
              <summary className="cursor-pointer select-none py-1 hover:text-gray-700">
                {t("free.result.emptyFields", { n: emptyFields.length })}
              </summary>
              <p className="mt-1 pl-4">{emptyFields.map((f) => fieldLabel(f)).join(" · ")}</p>
            </details>
          )}

          {/* Experimento «PDF escaneado limpio» (4-10-2026): solo con fotos. Ver ScanPdfPanel. */}
          {file && String(file.type || "").startsWith("image/") && (
            <ScanPdfPanel file={file} template={lastRun.current.template || template} t={t} lang={lang} />
          )}

          {/* Después de la tabla y antes del muro: p. ej. la vista previa del fichero de huéspedes. */}
          {resultAfter && <div className="mt-4">{resultAfter(fields)}</div>}

          {/* Conversion wall: viewing is free, taking it home needs an account */}
          <div ref={wallRef} className="mt-5 rounded-2xl border border-primary-100 bg-primary-50 p-5 text-center">
            <Lock className="mx-auto text-primary-600 mb-2" size={22} />
            <p className="text-base sm:text-lg text-primary-900 font-bold">
              {t(checkMode ? "invoiceCheck.wall.title" : "free.wall.title")}
            </p>
            <p className="text-sm text-primary-900/80 mt-1.5 mb-4 leading-relaxed">
              {checkMode
                ? t("invoiceCheck.wall.subtitle")
                : t(`free.wall.byTemplate.${wallKey}`, {
                    defaultValue: t("free.wall.subtitle"),
                  })}
            </p>
            <a
              href={signupHref}
              target="_blank"
              rel="noopener"
              onClick={() => trackSignup(checkMode ? "free_check" : "free_download")}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <Download size={18} />
              {t(checkMode ? "invoiceCheck.wall.cta" : "free.wall.cta")}
            </a>
            <p className="text-[11px] text-primary-600/70 mt-3">
              {t(checkMode ? "invoiceCheck.wall.note" : "free.wall.note")}
            </p>
          </div>

          <button
            onClick={() => {
              setFile(null);
              reset();
            }}
            className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
          >
            {t("free.result.again")}
          </button>
        </div>
      )}
    </div>
  );
}

const CHECK_STYLE = {
  ok: { icon: CheckCircle2, color: "text-primary-600", pill: "bg-primary-50 text-primary-700" },
  missing: { icon: XCircle, color: "text-red-600", pill: "bg-red-50 text-red-700" },
  unsure: { icon: HelpCircle, color: "text-amber-500", pill: "bg-amber-50 text-amber-800" },
  not_required: { icon: MinusCircle, color: "text-gray-300", pill: "bg-gray-100 text-gray-500" },
};

const VERDICT_STYLE = {
  pass: "border-primary-200 bg-primary-50 text-primary-900",
  fail: "border-red-200 bg-red-50 text-red-900",
  review: "border-amber-200 bg-amber-50 text-amber-900",
};

/**
 * Lista de requisitos de la factura (scanner_processor, `compliance`). Primero
 * lo que falta, luego lo dudoso, luego lo correcto y al final lo que no se
 * exige para ese importe: quien sube una factura quiere ver el problema.
 */
function CompliancePanel({ compliance, rules, notInvoice, t }) {
  if (compliance.error || !Array.isArray(compliance.checks)) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        {t("invoiceCheck.verdict.error")}
      </div>
    );
  }
  const r = compliance.rules || rules;
  const order = { missing: 0, unsure: 1, ok: 2, not_required: 3 };
  const checks = [...compliance.checks].sort((a, b) => order[a.status] - order[b.status]);
  return (
    <div data-testid="compliance" data-verdict={compliance.verdict}>
      <div className={`rounded-xl border p-4 ${VERDICT_STYLE[compliance.verdict] || VERDICT_STYLE.review}`}>
        <p className="font-semibold">{t(`invoiceCheck.verdict.${compliance.verdict}`)}</p>
        <p className="mt-1 text-sm opacity-90">{t(`invoiceCheck.verdictNote.${compliance.verdict}`)}</p>
        {compliance.mode === "small" && (
          <p className="mt-2 text-xs opacity-80">{t(`invoiceCheck.modeSmall.${r}`)}</p>
        )}
      </div>
      {notInvoice && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          {t("invoiceCheck.notInvoice")}
        </p>
      )}
      <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
        {checks.map((c) => {
          const st = CHECK_STYLE[c.status] || CHECK_STYLE.unsure;
          const Icon = st.icon;
          return (
            <li key={c.key} data-check={c.key} data-status={c.status} className="flex items-start gap-3 px-3 py-2.5 sm:px-4">
              <Icon size={18} className={`mt-0.5 shrink-0 ${st.color}`} />
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${c.status === "not_required" ? "text-gray-400" : "text-gray-900"}`}>
                  {t(`invoiceCheck.checks.${r}.${c.key}.label`)}
                </p>
                <p className="text-[11px] text-gray-400">{t(`invoiceCheck.checks.${r}.${c.key}.ref`)}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${st.pill}`}>
                {t(`invoiceCheck.status.${c.status}`)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[11px] text-gray-400">{t("invoiceCheck.disclaimer")}</p>
    </div>
  );
}

/**
 * Saldo inicial + movimientos = saldo final. Si cuadra, el visitante sabe que
 * no falta ninguna línea, que es la pregunta de fondo de quien pasa extractos a
 * Excel; si no, se le dice en qué fila mirar.
 */
function BalancePanel({ bc, t, lang }) {
  const n = (v) =>
    typeof v === "number" || (typeof v === "string" && v !== "")
      ? new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v))
      : "—";
  const style =
    bc.ok === true
      ? "border-primary-200 bg-primary-50 text-primary-900"
      : bc.ok === false
        ? "border-red-200 bg-red-50 text-red-900"
        : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div data-testid="balance-check" data-ok={String(bc.ok)} className={`mb-4 rounded-xl border p-4 ${style}`}>
      <p className="font-semibold">
        {bc.ok === true
          ? t("free.balance.ok", { rows: bc.rows })
          : bc.ok === false
            ? t("free.balance.fail", { diff: n(Math.abs(bc.difference)) })
            : t("free.balance.incomplete")}
      </p>
      {bc.ok !== null && (
        <p className="mt-1 text-sm tabular-nums opacity-90">
          {t("free.balance.formula", {
            opening: n(bc.opening),
            movements: n((bc.total_in || 0) + (bc.total_out || 0)),
            closing: n(bc.closing),
          })}
        </p>
      )}
      {bc.ok === false && bc.first_mismatch_row && (
        <p className="mt-1 text-sm">{t("free.balance.row", { row: bc.first_mismatch_row })}</p>
      )}
    </div>
  );
}

/**
 * «Cálculos» de la factura: neto + IVA = total, base × tipo = cuota, líneas.
 * Un fallo puede ser de la factura o de la lectura, así que se presenta como
 * algo que revisar, con las dos cifras a la vista.
 */
function ArithmeticPanel({ ar, t, lang }) {
  const n = (v) =>
    new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v));
  const bad = ar.checks.filter((c) => !c.ok);
  return (
    <div
      data-testid="arithmetic"
      data-ok={String(ar.ok)}
      className={`mt-3 rounded-xl border p-3 text-sm ${ar.ok ? "border-primary-200 bg-primary-50 text-primary-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
    >
      <p className="font-semibold">
        {ar.ok ? t("invoiceCheck.arith.ok", { count: ar.checks.length }) : t("invoiceCheck.arith.fail")}
      </p>
      {bad.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {bad.map((c, i) => (
            <li key={i} className="tabular-nums">
              {t(`invoiceCheck.arith.${c.key}`, {
                expected: n(c.expected),
                found: n(c.found),
                rate: c.rate,
                row: c.row,
              })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
